import React, { useCallback, useMemo, useState } from "react";
import { Loader } from "@design-system/Loader/Loader";
import { useEntities } from "@hooks/useEntities";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import {
    JsonParam,
    NumberParam,
    StringParam,
    useQueryParams,
} from "use-query-params";
import { transactionFiltersSchema } from "@common/schemas/transaction.schema";
import { TransactionView } from "@common/types/views/transactionViews";
import { NamedView } from "@common/types/views/views";
import { TransactionsFilters } from "./filters/lib";
import { PaginatedTransactionsTableContextProvider } from "./PaginatedTransactionsTableContextProvider";
import { TransactionsPage } from "./TransactionsPage";
import { resolveTransactionDeepLinkFilters } from "./lib/transactionsUrl";
import { useBuiltInTransactionViews } from "./views/BuiltInTransactionViews";
import { useTransactionsPageOpeningView } from "./views/useTransactionsPageOpeningView";
import { useDeepLinkGeneration } from "./useDeepLinkGeneration";
import { useTransactionsPageReady } from "./useTransactionsPageReady";

export const TransactionsPageContainer: React.FC = () => {
    const isReady = useTransactionsPageReady();
    const [page, setPage] = useState(1);

    const entities = useEntities();
    const entityIds = entities.map((entity) => entity.id);

    const builtInViews = useBuiltInTransactionViews();

    const [{ filters, view, transactionId }, setQueryParams] = useQueryParams({
        filters: JsonParam,
        view: StringParam,
        transactionId: NumberParam,
    });

    // Resolved once on mount alongside initialFilters so the deep-linked view's
    // columns and the deep-linked filters land in the same atomic assignment —
    // an effect-based selection would re-apply the view's own entity scope and
    // clobber the narrower scope the link asked for.
    const deepLinkedView = useMemo(
        () => builtInViews.find((builtInView) => builtInView.id === view),
        [builtInViews, view],
    );

    const initialFilters: TransactionsFilters | undefined = useMemo(() => {
        const parsedFilters = filters
            ? transactionFiltersSchema.safeParse(filters)
            : null;
        return resolveTransactionDeepLinkFilters({
            filters:
                parsedFilters?.success === true
                    ? parsedFilters.data
                    : undefined,
            entityIds,
            transactionId: transactionId ?? undefined,
        });
    }, [entityIds, filters, transactionId]);

    // The built-in list is flag-gated and LaunchDarkly resolves asynchronously,
    // so a deep link can arrive before the view it names exists. Hold both
    // params until the view resolves rather than consuming them on the first
    // mount — otherwise the link is silently dropped. Paired with the provider
    // `key` below, which re-runs the store initializer once it does resolve so
    // the view and its filters still land together.
    const hasUnresolvedView = view != null && deepLinkedView == null;

    const resetPage = useCallback(() => setPage(1), []);
    const deepLinkGeneration = useDeepLinkGeneration({
        hasDeepLink: filters != null,
        onNewDeepLink: resetPage,
    });

    const handleInitialFiltersConsumed = useCallback(() => {
        if (hasUnresolvedView) {
            return;
        }
        setQueryParams({ filters: undefined, view: undefined }, "replaceIn");
    }, [setQueryParams, hasUnresolvedView]);

    if (!isReady) {
        return <Loader />;
    }

    return (
        <TransactionsPageViewProvider
            key={`${deepLinkGeneration}:${deepLinkedView?.id ?? "no-deep-linked-view"}:${transactionId ?? "all-transactions"}`}
            page={page}
            setPage={setPage}
            deepLinkedView={deepLinkedView}
            initialFilters={initialFilters}
            onInitialFiltersConsumed={handleInitialFiltersConsumed}
        />
    );
};

interface TransactionsPageViewProviderProps {
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    deepLinkedView: NamedView<TransactionView> | undefined;
    initialFilters: TransactionsFilters | undefined;
    onInitialFiltersConsumed: () => void;
}

// Keyed by the container so every remount re-reads the view caches; opening
// state read above it would outlive the id the toolbar saves.
const TransactionsPageViewProvider: React.FC<
    TransactionsPageViewProviderProps
> = ({
    page,
    setPage,
    deepLinkedView,
    initialFilters,
    onInitialFiltersConsumed,
}) => {
    const { defaultView, baseViewIdToSave } = useTransactionsPageOpeningView();

    return (
        <PaginatedTransactionsTableContextProvider
            paginationMode={{ type: "pages", page, setPage }}
            defaultView={defaultView}
            cacheKey={
                WORKSPACE_USER_CACHE_KEYS.TRANSACTIONS_PAGE_VIEW_CACHE_KEY
            }
            initialFilters={initialFilters}
            initialView={deepLinkedView}
            initialBaseViewId={deepLinkedView?.id ?? baseViewIdToSave}
            onInitialFiltersConsumed={onInitialFiltersConsumed}
        >
            <TransactionsPage />
        </PaginatedTransactionsTableContextProvider>
    );
};
